/* Imvelo Roast Lab — the lab's engine.
   The roast model mirrors imvelo-origins.md (the canonical source): origin
   base scores blend by weight, the curve decides a development index R
   anchored at first crack (196°C → R 0, 236°C → R 1), and every readout —
   bean colour, level, radar, notes, warnings — derives from that one model.
   Mwamba (the conversational roaster) and naming call the Imvelo control
   tower on Railway at {endpoint}/api/roaster/*; the OpenRouter key lives
   there, never here. */
(function () {
  'use strict';
  if (window.__imvRoastLab) return;  // a second placement stays inert
  window.__imvRoastLab = true;
  var CFG = {};
  try { CFG = JSON.parse(document.getElementById('imv-lab-config').textContent); } catch (e) {}
  var ENDPOINT = String(CFG.endpoint || '').replace(/\/+$/, '');
  var API = ENDPOINT ? ENDPOINT + '/api/roaster' : '';
  var VARIANT_ID = CFG.variantId || '';
  var UNIT_PRICE = Number(CFG.unitPrice) || 0;
  var CURRENCY = CFG.currency || 'USD';
  var MIN_KG = Math.max(1, Number(CFG.minKg) || 2);
  var MAX_KG = Math.max(MIN_KG, Number(CFG.maxKg) || 20);

  var $ = function (id) { return document.getElementById(id); };
  var clamp = function (v, a, b) { return Math.max(a, Math.min(b, v)); };
  var cap = function (s) { return s.charAt(0).toUpperCase() + s.slice(1); };
  var fmtMin = function (x) { var m = Math.floor(x), s = Math.round((x - m) * 60); if (s === 60) { m += 1; s = 0; } return m + ':' + String(s).padStart(2, '0'); };
  /* Liquid money values arrive already in the buyer's presentment currency,
     so no Shopify.currency.rate multiplication here — that would convert twice. */
  var money = function (n) {
    var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || CURRENCY;
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(n); }
    catch (e) { return Math.round(n) + ' ' + cur; }
  };
  var escapeHtml = function (s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); };
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ===== ORIGIN DATA (mirror of imvelo-origins.md) ===== */
  var ORIGINS = {
    zambia: { name: 'Zambia', region: 'Kasama · Northern Province', base: { acid: 6, body: 6, sweet: 7, bitter: 3, fruit: 6, cocoa: 6 },
      notes: { light: ['green apple', 'jasmine', 'lemon zest'], medium: ['milk chocolate', 'caramel', 'orange'], dark: ['dark chocolate', 'molasses', 'toasted almond'] },
      blurb: 'Our home harvest from the Kasama highlands. Smooth and gently bright — soft citrus over a chocolate and caramel backbone, naturally low bitterness.' },
    zaire: { name: 'Zaire', region: 'Lake Kivu', base: { acid: 5, body: 8, sweet: 7, bitter: 4, fruit: 7, cocoa: 7 },
      notes: { light: ['red berry', 'hibiscus', 'grape'], medium: ['black cherry', 'cocoa', 'brown sugar'], dark: ['bittersweet chocolate', 'blackcurrant', 'pipe smoke'] },
      blurb: 'Grown in the hills above Lake Kivu. Lush and syrupy, wine-like depth on the heaviest body in the room.' },
    rwanda: { name: 'Rwanda', region: 'Huye · Southern Province', base: { acid: 8, body: 5, sweet: 6, bitter: 2, fruit: 8, cocoa: 4 },
      notes: { light: ['orange blossom', 'red currant', 'bergamot'], medium: ['red apple', 'honey', 'black tea'], dark: ['cocoa nib', 'dried fig', 'baking spice'] },
      blurb: 'Bourbon coffee from the high hills. Elegant and tea-like — floral aromatics over silky red fruit.' },
    zimbabwe: { name: 'Zimbabwe', region: 'Eastern Highlands', base: { acid: 8, body: 7, sweet: 6, bitter: 3, fruit: 8, cocoa: 5 },
      notes: { light: ['blackberry', 'lime', 'floral'], medium: ['berry compote', 'dark chocolate', 'winey'], dark: ['bittersweet chocolate', 'dried fig', 'bold spice'] },
      blurb: 'From the Eastern Highlands. Bold and vibrant, wine-like acidity on a full frame.' },
    tanzania: { name: 'Tanzania', region: 'Mbeya · Southern Highlands', base: { acid: 8, body: 6, sweet: 6, bitter: 3, fruit: 8, cocoa: 4 },
      notes: { light: ['black tea', 'citrus', 'jasmine'], medium: ['blackcurrant', 'brown sugar', 'orange'], dark: ['cocoa', 'molasses', 'warm spice'] },
      blurb: 'Peaberry beans from Mbeya and Kilimanjaro. Bright and winey with a snap of blackcurrant, a clean juicy finish.' }
  };
  var AXES = [['acid', 'Acidity'], ['body', 'Body'], ['sweet', 'Sweet'], ['bitter', 'Bitter'], ['fruit', 'Fruit'], ['cocoa', 'Cocoa']];
  var PRESETS = [
    { id: 'nordic', name: 'Nordic', pts: [{ t: 1.2, temp: 90 }, { t: 4.9, temp: 146 }, { t: 8.6, temp: 196 }, { t: 10.4, temp: 200 }] },
    { id: 'city', name: 'City', pts: [{ t: 1.3, temp: 90 }, { t: 5.3, temp: 150 }, { t: 8.8, temp: 196 }, { t: 11.0, temp: 205 }] },
    { id: 'cityplus', name: 'City+', pts: [{ t: 1.3, temp: 91 }, { t: 5.6, temp: 152 }, { t: 9.0, temp: 197 }, { t: 11.5, temp: 211 }] },
    { id: 'fullcity', name: 'Full City', pts: [{ t: 1.4, temp: 92 }, { t: 5.9, temp: 154 }, { t: 9.2, temp: 198 }, { t: 12.1, temp: 218 }] },
    { id: 'vienna', name: 'Vienna', pts: [{ t: 1.4, temp: 93 }, { t: 6.1, temp: 156 }, { t: 9.4, temp: 199 }, { t: 12.9, temp: 224 }] },
    { id: 'french', name: 'French', pts: [{ t: 1.5, temp: 94 }, { t: 6.4, temp: 158 }, { t: 9.6, temp: 200 }, { t: 13.7, temp: 228 }] },
    { id: 'italian', name: 'Italian', pts: [{ t: 1.5, temp: 95 }, { t: 6.7, temp: 160 }, { t: 9.8, temp: 201 }, { t: 14.5, temp: 233 }] }
  ];
  var CHARGE = { t: 0, temp: 168 };

  var state = { shares: {}, order: [], points: null, preset: 'cityplus', unit: 'C',
    qty: MIN_KG, mode: 'sculpt', selectedNotes: [], name: '', description: '' };
  function loadPreset(p) { state.points = [Object.assign({}, CHARGE)].concat(p.pts.map(function (o) { return Object.assign({}, o); })); }
  loadPreset(PRESETS[2]);

  /* ===== CURVE / ROAST MODEL ===== */
  var FC = 196, SC = 224, TMAX = 16, TY_MIN = 80, TY_MAX = 240;
  var CW = 560, CH = 330, PADL = 46, PADR = 18, PADT = 20, PADB = 40, PW = CW - PADL - PADR, PH = CH - PADT - PADB;
  var sx = function (t) { return PADL + (t / TMAX) * PW; }, sy = function (T) { return PADT + (1 - (T - TY_MIN) / (TY_MAX - TY_MIN)) * PH; };
  var ix = function (px) { return (px - PADL) / PW * TMAX; }, iy = function (py) { return TY_MIN + (1 - (py - PADT) / PH) * (TY_MAX - TY_MIN); };
  var C2F = function (c) { return c * 9 / 5 + 32; };
  var showTemp = function (c) { return state.unit === 'C' ? Math.round(c) + '°C' : Math.round(C2F(c)) + '°F'; };

  function monotone(xs, ys) {
    var n = xs.length, dx = [], m = [], d = new Array(n), i;
    for (i = 0; i < n - 1; i++) { dx[i] = xs[i + 1] - xs[i]; m[i] = (ys[i + 1] - ys[i]) / dx[i]; }
    d[0] = m[0]; d[n - 1] = m[n - 2];
    for (i = 1; i < n - 1; i++) d[i] = (m[i - 1] * m[i] <= 0) ? 0 : (m[i - 1] + m[i]) / 2;
    for (i = 0; i < n - 1; i++) { if (m[i] === 0) { d[i] = 0; d[i + 1] = 0; } else { var a = d[i] / m[i], b = d[i + 1] / m[i], h = a * a + b * b; if (h > 9) { var t = 3 / Math.sqrt(h); d[i] = t * a * m[i]; d[i + 1] = t * b * m[i]; } } }
    return function (x) {
      x = clamp(x, xs[0], xs[n - 1]); var k = n - 2;
      for (var j = 0; j < n - 1; j++) { if (x <= xs[j + 1]) { k = j; break; } }
      var hh = dx[k], tt = (x - xs[k]) / hh, t2 = tt * tt, t3 = t2 * tt;
      return (2 * t3 - 3 * t2 + 1) * ys[k] + (t3 - 2 * t2 + tt) * hh * d[k] + (-2 * t3 + 3 * t2) * ys[k + 1] + (t3 - t2) * hh * d[k + 1];
    };
  }

  function curveModel() {
    var P = state.points, xs = P.map(function (p) { return p.t; }), ys = P.map(function (p) { return p.temp; });
    var f = monotone(xs, ys), dropT = P[P.length - 1].t, dropTemp = P[P.length - 1].temp;
    var N = 180, samp = [];
    for (var i = 0; i <= N; i++) { var t = i / N * dropT; samp.push({ t: t, T: f(t) }); }
    var cross = function (target) {
      for (var j = 1; j < samp.length; j++) { if (samp[j - 1].T < target && samp[j].T >= target) { var a = samp[j - 1], b = samp[j]; return a.t + (b.t - a.t) * (target - a.T) / (b.T - a.T); } }
      return null;
    };
    return { f: f, samp: samp, dropT: dropT, dropTemp: dropTemp, fcT: cross(FC), scT: cross(SC) };
  }

  function model() {
    var keys = state.order, tot = keys.reduce(function (s, k) { return s + state.shares[k]; }, 0) || 1;
    var base = { acid: 0, body: 0, sweet: 0, bitter: 0, fruit: 0, cocoa: 0 };
    keys.forEach(function (k) { var w = state.shares[k] / tot; for (var a in base) base[a] += ORIGINS[k].base[a] * w; });
    var cm = curveModel();
    var R = clamp((cm.dropTemp - 196) / (236 - 196), 0, 1);
    var fcReached = cm.fcT !== null;
    var devTime = fcReached ? Math.max(0, cm.dropT - cm.fcT) : 0;
    var DTR = cm.dropT > 0 ? devTime / cm.dropT : 0;
    var f = {};
    f.acid = base.acid * (1.34 - 0.96 * R);
    f.body = base.body * (0.82 + 0.46 * R);
    f.sweet = base.sweet * (0.82 + 0.70 * (1 - 4 * Math.pow(R - 0.5, 2)));
    f.bitter = base.bitter + 6.5 * Math.pow(R, 1.7);
    f.fruit = base.fruit * (1.32 - 1.18 * R);
    f.cocoa = base.cocoa * (0.6 + 0.4 * R) + 4.4 * Math.pow(R, 1.35);
    var qual = 'good', qmsg = '';
    if (!fcReached) { qual = 'warn'; qmsg = 'This curve never reaches first crack — raw, grassy, undrinkable. Push the temperature higher.'; f.acid *= 1.1; f.sweet *= 0.5; f.fruit *= 0.6; }
    else if (DTR < 0.12) { qual = 'warn'; qmsg = 'Very short development after first crack — sharp and a touch sour, the origin not fully resolved.'; f.sweet *= 0.8; f.acid *= 1.08; }
    else if (DTR > 0.40 && R > 0.55) { qual = 'warn'; qmsg = 'A long bake at high heat — flavours flatten toward papery and ashy.'; f.acid *= 0.7; f.sweet *= 0.8; f.fruit *= 0.7; f.bitter *= 1.1; }
    else if (DTR > 0.40) { qual = 'warn'; qmsg = 'Stalled, baked development — the cup turns flat and bready.'; f.sweet *= 0.78; f.fruit *= 0.8; f.acid *= 0.85; }
    else { qual = 'good'; qmsg = 'Nicely developed — sugars resolved, acidity and body in balance.'; f.sweet *= 1.06; }
    for (var a in f) f[a] = clamp(f[a], 0.4, 10);
    var level, sub;
    if (!fcReached) { level = 'Under-developed'; sub = 'never hit first crack'; }
    else if (R < 0.06) { level = 'Cinnamon'; sub = 'very light · grassy-bright'; }
    else if (R < 0.17) { level = 'Light · Nordic'; sub = 'origin-forward, maximum clarity'; }
    else if (R < 0.31) { level = 'Light–Medium · City'; sub = 'bright and balanced'; }
    else if (R < 0.46) { level = 'Medium · City+'; sub = 'smooth — the all-rounder'; }
    else if (R < 0.63) { level = 'Medium · Full City'; sub = 'caramel and cocoa, rounded'; }
    else if (R < 0.75) { level = 'Medium–Dark · Vienna'; sub = 'deep, into second crack'; }
    else if (R < 0.87) { level = 'Dark · French'; sub = 'bittersweet and smoky'; }
    else { level = 'Very Dark · Italian'; sub = 'roast-forward, oily'; }
    var band = R < 0.30 ? 'light' : (R < 0.72 ? 'medium' : 'dark');
    var noteScore = {};
    keys.forEach(function (k) { var w = state.shares[k] / tot; ORIGINS[k].notes[band].forEach(function (n, i) { noteScore[n] = (noteScore[n] || 0) + w * (3 - i); }); });
    var notes = Object.keys(noteScore).sort(function (a, b) { return noteScore[b] - noteScore[a]; }).slice(0, 5);
    return Object.assign({}, cm, { f: f, R: R, devTime: devTime, DTR: DTR, fcReached: fcReached, level: level, sub: sub, qual: qual, qmsg: qmsg, notes: notes, band: band });
  }

  /* ===== BLEND STATE ===== */
  function renorm() { var s = state.order.reduce(function (a, k) { return a + state.shares[k]; }, 0); if (s > 0) state.order.forEach(function (k) { state.shares[k] = state.shares[k] * 100 / s; }); }
  function pctMap() {
    var tot = state.order.reduce(function (s, k) { return s + state.shares[k]; }, 0) || 1;
    var rows = state.order.map(function (k) { var v = state.shares[k] / tot * 100; return { k: k, f: Math.floor(v), r: v - Math.floor(v) }; });
    var rem = 100 - rows.reduce(function (s, o) { return s + o.f; }, 0);
    rows.slice().sort(function (a, b) { return b.r - a.r; }).forEach(function (o, i) { if (i < rem) o.f += 1; });
    var out = {}; rows.forEach(function (o) { out[o.k] = o.f; }); return out;
  }
  function reorder() { state.order.sort(function (a, b) { return state.shares[b] - state.shares[a]; }); }
  function toggleOrigin(k) {
    if (state.shares[k] !== undefined) { delete state.shares[k]; state.order = state.order.filter(function (x) { return x !== k; }); renorm(); }
    else {
      var n = state.order.length;
      if (n === 0) state.shares[k] = 100;
      else { var give = Math.round(100 / (n + 1)), scale = (100 - give) / 100; state.order.forEach(function (j) { state.shares[j] *= scale; }); state.shares[k] = give; renorm(); }
      state.order.push(k);
    }
    reorder(); renderWeights(true); render(); refreshPalette();
  }
  function setShare(k, v) {
    v = clamp(v, 1, 99);
    var others = state.order.filter(function (x) { return x !== k; });
    if (!others.length) { state.shares[k] = 100; return; }
    var oldOthers = 100 - state.shares[k], newOthers = 100 - v;
    others.forEach(function (j) { state.shares[j] = oldOthers > 0 ? state.shares[j] * newOthers / oldOthers : newOthers / others.length; });
    state.shares[k] = v; renorm();
  }

  /* ===== ORIGIN LIST UI ===== */
  var elOrigins = $('origins');
  Object.keys(ORIGINS).forEach(function (key) {
    var o = ORIGINS[key], b = document.createElement('button');
    b.className = 'imv-origin'; b.type = 'button'; b.dataset.k = key;
    b.setAttribute('aria-pressed', 'false');
    b.innerHTML = '<span class="imv-origin__name">' + o.name + '</span>' +
      '<span class="imv-origin__check" aria-hidden="true">+</span>' +
      '<span class="imv-origin__region">' + o.region + '</span>' +
      '<span class="imv-origin__blurb">' + o.blurb + '</span>';
    b.addEventListener('click', function () { toggleOrigin(key); });
    elOrigins.appendChild(b);
  });
  var sliderRefs = {};
  function renderWeights(rebuild) {
    document.querySelectorAll('.imv-origin').forEach(function (o) {
      var on = state.shares[o.dataset.k] !== undefined;
      o.classList.toggle('on', on); o.setAttribute('aria-pressed', on ? 'true' : 'false');
      o.querySelector('.imv-origin__check').textContent = on ? '✓' : '+';
    });
    var w = $('weights');
    if (!state.order.length) { w.innerHTML = ''; return; }
    if (rebuild) {
      w.innerHTML = '';
      var P = pctMap(), single = state.order.length === 1;
      if (single) { w.innerHTML = '<div class="imv-weights__note">' + ORIGINS[state.order[0]].name + ', single origin. Add a second origin to blend.</div>'; return; }
      var lbl = document.createElement('div'); lbl.className = 'imv-weights__label'; lbl.textContent = 'Balance the blend'; w.appendChild(lbl);
      state.order.forEach(function (k) {
        var row = document.createElement('div'); row.className = 'imv-wrow';
        row.innerHTML = '<label for="w-' + k + '">' + ORIGINS[k].name + '</label>' +
          '<input id="w-' + k + '" type="range" min="1" max="99" value="' + P[k] + '"' + (single ? ' disabled' : '') +
          ' aria-label="' + ORIGINS[k].name + ' share of the blend">' +
          '<span class="pct" data-k="' + k + '">' + P[k] + '%</span>';
        var inp = row.querySelector('input'); sliderRefs[k] = inp;
        inp.addEventListener('input', function (e) { setShare(k, +e.target.value); syncSliders(); render(); });
        inp.addEventListener('change', function () { reorder(); renderWeights(true); render(); });
        w.appendChild(row);
      });
    } else syncSliders();
  }
  function syncSliders() {
    var P = pctMap();
    state.order.forEach(function (k) { if (sliderRefs[k]) sliderRefs[k].value = P[k]; });
    document.querySelectorAll('.imv-wrow .pct').forEach(function (el) { var k = el.dataset.k; if (P[k] !== undefined) el.textContent = P[k] + '%'; });
  }

  /* ===== PRESETS ===== */
  /* The roast level is one continuous slider, light to dark, over the seven
     presets: between two presets the curve is the blend of both, so the
     lever moves the roast smoothly instead of jumping. A hand-shaped curve
     reads as the nearest preset, marked "custom", and the thumb sits where
     its drop temperature falls. */
  var levelSlider = $('levelSlider');
  var LAST = PRESETS.length - 1;
  function presetIndex(id) { for (var i = 0; i < PRESETS.length; i++) { if (PRESETS[i].id === id) return i; } return -1; }
  function nearestPreset(dropTemp) { var best = 0, d = 1e9; PRESETS.forEach(function (p, i) { var dd = Math.abs(p.pts[3].temp - dropTemp); if (dd < d) { d = dd; best = i; } }); return best; }
  /* where a drop temperature sits on the lever, fractional between presets */
  function levelPosition(dropTemp) {
    if (dropTemp <= PRESETS[0].pts[3].temp) return 0;
    for (var i = 0; i < LAST; i++) {
      var a = PRESETS[i].pts[3].temp, b = PRESETS[i + 1].pts[3].temp;
      if (dropTemp <= b) return i + (dropTemp - a) / (b - a);
    }
    return LAST;
  }
  function levelText(v) {
    var i = Math.floor(v), t = v - i;
    if (i >= LAST || t < 0.08) return PRESETS[Math.round(v)].name;
    if (t > 0.92) return PRESETS[i + 1].name;
    return 'between ' + PRESETS[i].name + ' and ' + PRESETS[i + 1].name;
  }
  function syncLevel(m) {
    var v = state.preset && state.levelPos !== null && state.levelPos !== undefined ? state.levelPos : levelPosition(m.dropTemp);
    levelSlider.value = v;
    levelSlider.setAttribute('aria-valuetext', levelText(v) + (state.preset ? '' : ', custom curve'));
    $('levelName').textContent = m.level;
    $('levelSub').textContent = m.sub + (state.preset ? '' : ' · custom curve');
  }
  /* the lever: a fractional level blends the two presets either side of it */
  function setLevel(v) {
    v = clamp(+v || 0, 0, LAST);
    var i = Math.min(Math.floor(v), LAST - 1), t = v - i, a = PRESETS[i].pts, b = PRESETS[i + 1].pts;
    cancelAnimationFrame(anim);
    for (var k = 0; k < 4; k++) {
      state.points[k + 1].t = a[k].t + (b[k].t - a[k].t) * t;
      state.points[k + 1].temp = a[k].temp + (b[k].temp - a[k].temp) * t;
    }
    state.preset = PRESETS[Math.round(v)].id; state.levelPos = v;
    render();
  }
  levelSlider.addEventListener('input', function () { setLevel(levelSlider.value); });
  /* keys move a quarter level at a time; the browser's own step is too fine to be useful */
  levelSlider.addEventListener('keydown', function (e) {
    var d = { ArrowRight: 0.25, ArrowUp: 0.25, ArrowLeft: -0.25, ArrowDown: -0.25, PageUp: 1, PageDown: -1 }[e.key];
    if (d === undefined) return;
    e.preventDefault(); setLevel(Math.round((+levelSlider.value + d) * 4) / 4);
  });
  var anim = null;
  function applyPreset(p) {
    state.preset = p.id; state.levelPos = presetIndex(p.id);
    var from = state.points.slice(1).map(function (o) { return Object.assign({}, o); }), to = p.pts;
    if (reduceMotion) { for (var i = 0; i < 4; i++) { state.points[i + 1].t = to[i].t; state.points[i + 1].temp = to[i].temp; } render(); return; }
    cancelAnimationFrame(anim); var t0 = performance.now(), dur = 380;
    (function step(now) {
      var r = clamp((now - t0) / dur, 0, 1), e = r < .5 ? 2 * r * r : 1 - Math.pow(-2 * r + 2, 2) / 2;
      for (var i = 0; i < 4; i++) { state.points[i + 1].t = from[i].t + (to[i].t - from[i].t) * e; state.points[i + 1].temp = from[i].temp + (to[i].temp - from[i].temp) * e; }
      render(); if (r < 1) anim = requestAnimationFrame(step);
    })(t0);
  }
  function clearPresetActive() {
    state.preset = null; state.levelPos = null;
    cancelAnimationFrame(anim);  // manual control interrupts any preset glide
  }

  /* ===== CURVE (ink on paper: cream line, gold for the cracks) ===== */
  var curve = $('curve');
  var HANDLES = [{ i: 1, label: 'Turning point' }, { i: 2, label: 'Browning' }, { i: 3, label: 'Development' }, { i: 4, label: 'OUT' }];
  function buildCurve(m) {
    var path = '';
    m.samp.forEach(function (s, i) { path += (i ? 'L' : 'M') + sx(s.t).toFixed(1) + ' ' + sy(clamp(s.T, TY_MIN, TY_MAX)).toFixed(1) + ' '; });
    var fcY = sy(FC), scY = sy(SC), ticks = '', xticks = '', v, t;
    for (v = 80; v <= 240; v += 40) { var y = sy(v); ticks += '<line x1="' + PADL + '" y1="' + y + '" x2="' + (CW - PADR) + '" y2="' + y + '" stroke="rgba(20,31,56,0.10)" stroke-width="1"/><text x="' + (PADL - 8) + '" y="' + (y + 4) + '" text-anchor="end" font-size="10" fill="rgba(20,31,56,0.62)" font-family="Space Mono, monospace">' + showTemp(v) + '</text>'; }
    for (t = 0; t <= TMAX; t += 2) { xticks += '<text x="' + sx(t) + '" y="' + (CH - 13) + '" text-anchor="middle" font-size="10" fill="rgba(20,31,56,0.62)" font-family="Space Mono, monospace">' + t + 'm</text>'; }
    var devBand = '';
    if (m.fcT !== null) {
      devBand = '<rect x="' + sx(m.fcT) + '" y="' + PADT + '" width="' + (sx(m.dropT) - sx(m.fcT)) + '" height="' + PH + '" fill="rgba(224,164,59,0.14)"/>';
      if (sx(m.dropT) - sx(m.fcT) > 70) {
        devBand += '<text x="' + ((sx(m.fcT) + sx(m.dropT)) / 2) + '" y="' + (PADT + PH - 8) + '" text-anchor="middle" font-size="9" letter-spacing="2" fill="#B98214" font-family="Space Mono, monospace">DEVELOPMENT</text>';
      }
    }
    var cracks = '<line x1="' + PADL + '" y1="' + fcY + '" x2="' + (CW - PADR) + '" y2="' + fcY + '" stroke="rgba(185,130,20,0.7)" stroke-width="1" stroke-dasharray="5 4"/>' +
      '<text x="' + (PADL + 4) + '" y="' + (fcY - 6) + '" font-size="9.5" fill="#B98214" font-family="Space Mono, monospace">FIRST CRACK · ' + showTemp(FC) + '</text>' +
      '<line x1="' + PADL + '" y1="' + scY + '" x2="' + (CW - PADR) + '" y2="' + scY + '" stroke="rgba(185,130,20,0.35)" stroke-width="1" stroke-dasharray="5 4"/>' +
      '<text x="' + (PADL + 4) + '" y="' + (scY - 6) + '" font-size="9.5" fill="rgba(185,130,20,0.75)" font-family="Space Mono, monospace">SECOND CRACK · ' + showTemp(SC) + '</text>';
    if (m.fcT !== null) cracks += '<circle cx="' + sx(m.fcT) + '" cy="' + fcY + '" r="4" fill="#B98214" stroke="#F7F2E7" stroke-width="1.5"/>';
    if (m.scT !== null) cracks += '<circle cx="' + sx(m.scT) + '" cy="' + scY + '" r="4" fill="rgba(185,130,20,0.6)" stroke="#F7F2E7" stroke-width="1.5"/>';
    var coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    var handles = '';
    HANDLES.forEach(function (h) {
      var p = state.points[h.i], x = sx(p.t), yy = sy(p.temp), isOut = h.i === 4;
      var r = isOut ? (coarse ? 15 : 11) : (coarse ? 12 : 8);
      var labelY = Math.max(12, yy - (isOut ? r + 7 : r + 6));
      handles += '<circle class="hnd" data-i="' + h.i + '" tabindex="0" role="slider" aria-label="' + (isOut ? 'Drop temperature' : h.label) + '" aria-valuemin="78" aria-valuemax="236" aria-valuenow="' + Math.round(p.temp) + '" aria-valuetext="' + Math.round(p.temp) + ' degrees at ' + fmtMin(p.t) + '" cx="' + x + '" cy="' + yy + '" r="' + r + '" fill="' + (isOut ? '#E0A43B' : '#FCFAF4') + '" stroke="#141F38" stroke-width="2" style="cursor:grab"/>' +
        '<text x="' + x + '" y="' + labelY + '" text-anchor="middle" font-size="' + (isOut ? 10 : 9.5) + '" fill="rgba(20,31,56,0.85)" font-family="Space Mono, monospace" style="pointer-events:none">' + (isOut ? ('OUT ' + showTemp(p.temp)) : h.label.toUpperCase()) + '</text>';
    });
    curve.innerHTML = ticks + xticks + devBand + cracks +
      '<path d="' + path + '" fill="none" stroke="#141F38" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/>' + handles;
    curve.querySelectorAll('.hnd').forEach(function (c) {
      c.addEventListener('mousedown', startDrag);
      // Touch events keep firing on the element the touch STARTED on, and
      // render() rebuilds the SVG (detaching that element) — so the move/end
      // listeners must live on the handle itself, not only on window.
      c.addEventListener('touchstart', startDrag, { passive: false });
      c.addEventListener('touchmove', moveDrag, { passive: false });
      c.addEventListener('touchend', endDrag);
      c.addEventListener('touchcancel', endDrag);
      c.addEventListener('keydown', keyNudge);
    });
  }
  var dragI = null;
  function pt(e) {
    var r = curve.getBoundingClientRect();
    var cx = e.touches ? e.touches[0].clientX : e.clientX, cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (cx - r.left) / r.width * CW, y: (cy - r.top) / r.height * CH };
  }
  function startDrag(e) { dragI = +e.currentTarget.dataset.i; clearPresetActive(); e.preventDefault(); }
  function moveDrag(e) {
    if (dragI === null) return;
    var p = pt(e), c = state.points[dragI];
    c.t = ix(p.x); c.temp = iy(p.y); constrain(dragI); render();
    if (e.preventDefault) e.preventDefault();
  }
  function endDrag() { dragI = null; }
  window.addEventListener('mousemove', moveDrag); window.addEventListener('mouseup', endDrag);
  window.addEventListener('touchmove', moveDrag, { passive: false }); window.addEventListener('touchend', endDrag);
  window.addEventListener('touchcancel', endDrag);
  function keyNudge(e) {
    var i = +e.currentTarget.dataset.i, c = state.points[i], step = e.shiftKey ? 5 : 1, used = true;
    if (e.key === 'ArrowUp') c.temp += step;
    else if (e.key === 'ArrowDown') c.temp -= step;
    else if (e.key === 'ArrowLeft') c.t -= step * 0.2;
    else if (e.key === 'ArrowRight') c.t += step * 0.2;
    else used = false;
    if (used) { clearPresetActive(); constrain(i); render(); var again = curve.querySelector('.hnd[data-i="' + i + '"]'); if (again) again.focus(); e.preventDefault(); }
  }
  function constrain(i) {
    var P = state.points, c = P[i];
    if (i === 1) { c.t = clamp(c.t, 0.6, Math.min(3, P[2].t - 0.6)); c.temp = clamp(c.temp, 78, Math.min(120, P[2].temp - 8)); }
    else if (i === 2) { c.t = clamp(c.t, P[1].t + 0.6, P[3].t - 0.6); c.temp = clamp(c.temp, Math.max(118, P[1].temp + 8), Math.min(192, P[3].temp - 5)); }
    else if (i === 3) { c.t = clamp(c.t, P[2].t + 0.5, P[4].t - 0.4); c.temp = clamp(c.temp, Math.max(178, P[2].temp + 5), Math.min(218, P[4].temp - 2)); }
    else if (i === 4) { c.t = clamp(c.t, P[3].t + 0.4, TMAX); c.temp = clamp(c.temp, Math.max(192, P[3].temp + 2), 236); }
  }

  /* ===== THE CUP: six tastes as bars ===== */
  var profileEl = $('profile');
  function tasteWord(v) { return v < 3.5 ? 'low' : v < 5 ? 'gentle' : v < 6.8 ? 'medium' : v < 8.4 ? 'high' : 'bold'; }
  function buildProfile(m) {
    if (!profileEl) return;
    if (!m) { profileEl.innerHTML = ''; return; }
    if (!profileEl.children.length) {
      profileEl.innerHTML = AXES.map(function (ax) {
        return '<div class="imv-profile__row" data-ax="' + ax[0] + '"><span>' + ax[1] + '</span>' +
          '<span class="imv-profile__track"><span class="imv-profile__fill" style="width:0%"></span></span>' +
          '<span class="imv-profile__val"></span></div>';
      }).join('');
    }
    AXES.forEach(function (ax) {
      var row = profileEl.querySelector('[data-ax="' + ax[0] + '"]'), v = m.f[ax[0]];
      row.querySelector('.imv-profile__fill').style.width = Math.round(v * 10) + '%';
      row.querySelector('.imv-profile__val').textContent = tasteWord(v);
      row.setAttribute('aria-label', ax[1] + ' ' + v.toFixed(1) + ' of 10');
    });
  }
  var radar = $('radar');
  function buildRadar(m) {
    if (!radar) return;
    var cx = 150, cy = 124, Rr = 92, n = AXES.length, rings = '', axes = '', labels = '', g, i;
    for (g = 1; g <= 4; g++) { var rr = Rr * g / 4, pts = ''; for (i = 0; i < n; i++) { var a = -Math.PI / 2 + i * 2 * Math.PI / n; pts += (cx + rr * Math.cos(a)).toFixed(1) + ',' + (cy + rr * Math.sin(a)).toFixed(1) + ' '; } rings += '<polygon points="' + pts + '" fill="none" stroke="rgba(20,31,56,0.14)" stroke-width="1"/>'; }
    AXES.forEach(function (ax, j) {
      var a = -Math.PI / 2 + j * 2 * Math.PI / n, ex = cx + Rr * Math.cos(a), ey = cy + Rr * Math.sin(a);
      axes += '<line x1="' + cx + '" y1="' + cy + '" x2="' + ex + '" y2="' + ey + '" stroke="rgba(20,31,56,0.14)" stroke-width="1"/>';
      var lr = Rr + 20, lx = cx + lr * Math.cos(a), ly = cy + lr * Math.sin(a);
      var anchor = Math.abs(Math.cos(a)) < 0.3 ? 'middle' : (Math.cos(a) > 0 ? 'start' : 'end');
      labels += '<text x="' + lx + '" y="' + (ly + 4) + '" text-anchor="' + anchor + '" font-size="11" fill="rgba(20,31,56,0.6)" font-family="Hanken Grotesk, sans-serif" font-weight="400">' + ax[1] + '</text>';
    });
    var poly = '', dots = '';
    AXES.forEach(function (ax, j) {
      var a = -Math.PI / 2 + j * 2 * Math.PI / n, rr = Rr * (m.f[ax[0]] / 10);
      var px = (cx + rr * Math.cos(a)).toFixed(1), py = (cy + rr * Math.sin(a)).toFixed(1);
      poly += px + ',' + py + ' ';
      dots += '<circle cx="' + px + '" cy="' + py + '" r="3" fill="#141F38"/>';
    });
    radar.innerHTML = rings + axes + '<polygon points="' + poly + '" fill="rgba(224,164,59,0.22)" stroke="#B98214" stroke-width="1.6" stroke-linejoin="round"/>' + dots + labels;
  }

  /* ===== BEAN ===== */
  var STOPS = [[0, [216, 178, 122]], [0.2, [192, 138, 78]], [0.4, [154, 95, 51]], [0.6, [111, 64, 34]], [0.78, [77, 44, 27]], [0.9, [50, 31, 21]], [1, [33, 22, 16]]];
  function beanColor(R) {
    for (var i = 0; i < STOPS.length - 1; i++) {
      var a = STOPS[i][0], ca = STOPS[i][1], b = STOPS[i + 1][0], cb = STOPS[i + 1][1];
      if (R <= b) { var t = (R - a) / (b - a); return 'rgb(' + ca.map(function (c, j) { return Math.round(c + (cb[j] - c) * t); }).join(',') + ')'; }
    }
    return 'rgb(33,22,16)';
  }

  /* ===== MASTER RENDER ===== */
  function roastTitle() { return state.name || (state.order.length === 1 ? ORIGINS[state.order[0]].name + ' single origin' : 'Your custom blend'); }
  function syncTitle() { var t = $('cupTitle'); if (t) t.textContent = roastTitle(); }
  function render() {
    var has = state.order.length > 0, m = model();
    var cupCard = $('panel-cup'); if (cupCard) cupCard.classList.toggle('is-empty', !has);
    buildCurve(m);
    buildProfile(has ? m : null);
    if (radar) { if (has) buildRadar(m); else radar.innerHTML = ''; }
    var beanBg;
    if (has) {
      $('roastLevel').textContent = m.level;
      $('roastSub').textContent = m.sub;
      $('bandHint').textContent = m.band + ' roast';
      $('mTime').textContent = fmtMin(m.dropT);
      $('mFC').textContent = m.fcReached ? fmtMin(m.fcT) : '—';
      $('mDev').textContent = m.fcReached ? (fmtMin(m.devTime) + ' · ' + Math.round(m.DTR * 100) + '%') : '—';
      $('mDrop').textContent = showTemp(m.dropTemp);
      var q = $('quality'); q.className = 'imv-quality ' + m.qual; q.textContent = m.qmsg;
      beanBg = m.fcReached ? beanColor(m.R) : beanColor(0.04);
      $('beancol').style.background = beanBg;
      $('sheen').style.opacity = m.R > 0.82 ? (0.4 + (m.R - 0.82) * 2) : 0;
      var chipsEl = $('chips'), n3 = m.notes.slice(0, 3), rest = m.notes.slice(3);
      var lead = n3.length ? '<span class="lead">' + n3[0] + '</span>' : '';
      var mids = n3.slice(1);
      var sentence = lead + (mids.length ? (mids.length > 1 ? ', ' + mids.slice(0, -1).join(', ') : '') + ' and ' + mids[mids.length - 1] : '');
      if (rest.length) sentence += ', with ' + (rest.length > 1 ? rest.slice(0, -1).join(', ') + ' and ' + rest[rest.length - 1] : rest[0]);
      chipsEl.innerHTML = sentence ? 'Tastes of ' + sentence + '.' : '';
      var Pm = pctMap();
      var blendLine = state.order.map(function (k) { return ORIGINS[k].name + ' ' + Pm[k]; }).join(' · ');
      $('cartMeta').textContent = blendLine;
      /* Mwamba's bag copy describes one roast; when the roast moves on, the copy steps aside */
      var sig = state.order.map(function (k) { return k + ':' + Pm[k]; }).join('|') + '|' + m.level;
      if (state.description && !state.descSig) state.descSig = sig;
      if (state.description && state.descSig !== sig) { state.description = ''; state.descSig = ''; $('namedesc').textContent = ''; $('namedesc').hidden = true; }
    } else {
      $('roastLevel').textContent = 'Your roast starts here'; $('roastSub').textContent = 'Choose an origin, or tell Mwamba the cup you want.'; $('bandHint').textContent = '';
      ['mTime', 'mFC', 'mDev', 'mDrop'].forEach(function (id) { $(id).textContent = '—'; });
      var q0 = $('quality'); q0.className = 'imv-quality'; q0.textContent = '';
      beanBg = '';
      $('beancol').style.background = ''; $('sheen').style.opacity = 0;
      $('chips').innerHTML = '';
      $('cartMeta').textContent = 'Your custom roast · choose an origin';
    }
    syncTitle();
    var dockBean = $('dockBean'); if (dockBean) dockBean.style.background = beanBg || '';
    var dockLevel = $('dockLevel'); if (dockLevel) dockLevel.textContent = has ? m.level.split(' · ').pop() : 'Your roast';
    syncLevel(m);
    renderSpec(has ? m : null);
    updatePrice(); persistSoon();
  }
  function renderSpec(m) {
    var host = $('specblock'); if (!host) return;
    if (!m) { host.innerHTML = '<div class="imv-empty">Appears once an origin is in the blend.</div>'; return; }
    var P = pctMap();
    var rows = [
      ['Blend', state.order.map(function (k) { return ORIGINS[k].name + ' ' + P[k] + '%'; }).join(' · ')],
      ['Roast level', m.level],
      ['Drop', showTemp(m.dropTemp) + ' at ' + fmtMin(m.dropT)],
      ['First crack', m.fcReached ? fmtMin(m.fcT) : '—'],
      ['Development', m.fcReached ? (fmtMin(m.devTime) + ' · DTR ' + Math.round(m.DTR * 100) + '%') : '—'],
      ['Notes', m.notes.slice(0, 3).map(cap).join(', ')],
      ['Provenance', 'African highlands · washed arabica'],
      ['Roasted', 'To order, by hand, in Lusaka']
    ];
    host.innerHTML = rows.map(function (r) { return '<div class="imv-spec"><span class="k">' + r[0] + '</span><span class="v">' + escapeHtml(r[1]) + '</span></div>'; }).join('');
  }

  /* ===== PRICE / CART ===== */
  function updatePrice() {
    var total = state.order.length ? UNIT_PRICE * state.qty : 0;
    $('qty').textContent = state.qty + ' kg';
    $('price').textContent = total ? money(total) : '—';
    var dp = $('dockPrice'); if (dp) dp.textContent = total ? (state.qty + ' kg · ' + money(total)) : 'Choose an origin';
    /* the per-kilo rate sits under the price; the long note only speaks before the first origin */
    var unit = $('unitNote'); if (unit) unit.textContent = total ? money(UNIT_PRICE) + ' per kg · one batch' : '';
    var note = $('orderNote');
    if (note) note.textContent = state.order.length ? '' : 'Roasted to order in Lusaka · minimum ' + MIN_KG + ' kg per batch';
  }
  $('plus').addEventListener('click', function () { state.qty = Math.min(MAX_KG, state.qty + 1); updatePrice(); });
  $('minus').addEventListener('click', function () { state.qty = Math.max(MIN_KG, state.qty - 1); updatePrice(); });

  function toast(msg) {
    var t = document.getElementById('imv-toast');
    if (!t) { t = document.createElement('div'); t.id = 'imv-toast'; t.className = 'imv-toast'; document.body.appendChild(t); }
    t.textContent = msg; t.style.opacity = '1';
    clearTimeout(t._h); t._h = setTimeout(function () { t.style.opacity = '0'; }, 3600);
  }

  $('addcart').addEventListener('click', function () {
    if (!state.order.length) { toast('Add at least one origin to your blend first.'); return; }
    var m = model(), Pc = pctMap();
    var properties = {
      'Blend': roastTitle(),
      'Origins': state.order.map(function (k) { return ORIGINS[k].name + ' ' + Pc[k] + '%'; }).join(', '),
      'Roast': m.level,
      'Tasting notes': m.notes.join(', '),
      '_Drop temperature': Math.round(m.dropTemp) + '°C',
      '_First crack': m.fcReached ? fmtMin(m.fcT) : '—',
      '_Development': m.fcReached ? (fmtMin(m.devTime) + ' (DTR ' + Math.round(m.DTR * 100) + '%)') : '—',
      '_Total roast time': fmtMin(m.dropT),
      '_Roast link': shareUrl()
    };
    if (!VARIANT_ID) {
      if (window.Shopify && Shopify.designMode) toast('Preview — on the live store this adds ' + state.qty + ' kg of your ' + m.level + ' roast to the cart.');
      else toast('Ordering is briefly unavailable — the made-to-order product is not connected. Save or share your roast and try again soon.');
      return;
    }
    var btn = $('addcart'); btn.disabled = true; var old = btn.textContent; btn.textContent = 'Adding…';
    fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: VARIANT_ID, quantity: state.qty, properties: properties }] }) })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (er) { throw new Error(er.description || er.message || 'Could not add to cart.'); }, function () { throw new Error('Could not add to cart.'); });
        window.location.href = '/cart';
      })
      .catch(function (e) { toast(e.message || 'Could not add to cart.'); btn.disabled = false; btn.textContent = old; });
  });

  $('unitC').addEventListener('click', function () { state.unit = 'C'; $('unitC').setAttribute('aria-pressed', 'true'); $('unitF').setAttribute('aria-pressed', 'false'); render(); });
  $('unitF').addEventListener('click', function () { state.unit = 'F'; $('unitF').setAttribute('aria-pressed', 'true'); $('unitC').setAttribute('aria-pressed', 'false'); render(); });

  /* ===== TASTE MODE ===== */
  var bandIdx = { light: 0, medium: 1, dark: 2 };
  function availableNotes() {
    var map = {};
    state.order.forEach(function (k) { ['light', 'medium', 'dark'].forEach(function (band) { ORIGINS[k].notes[band].forEach(function (n) {
      if (!map[n]) map[n] = { bands: [], origins: [] };
      if (map[n].bands.indexOf(band) < 0) map[n].bands.push(band);
      if (map[n].origins.indexOf(k) < 0) map[n].origins.push(k);
    }); }); });
    return map;
  }
  function noteBand(map, n) { var arr = map[n].bands.map(function (b) { return bandIdx[b]; }); return arr.reduce(function (a, c) { return a + c; }, 0) / arr.length; }
  var tasteBox = $('tasteControls');
  function setMode(mo) { state.mode = mo; if (mo === 'taste') renderPalette(); }
  tasteBox.addEventListener('toggle', function () { setMode(tasteBox.open ? 'taste' : 'sculpt'); });
  function refreshPalette() {
    var map = availableNotes();
    state.selectedNotes = state.selectedNotes.filter(function (n) { return map[n]; });
    if (state.mode === 'taste') renderPalette();
  }
  function renderPalette() {
    var host = $('palette'), back = $('tasteBack');
    if (!state.order.length) { host.innerHTML = '<div class="imv-empty">Choose an origin first. Its flavours appear here.</div>'; back.hidden = true; $('tasteCnt').textContent = '0 / 5'; return; }
    var map = availableNotes(), groups = { 0: [], 1: [], 2: [] };
    Object.keys(map).forEach(function (n) { groups[Math.round(noteBand(map, n))].push(n); });
    var lbl = { 0: 'Bright · light roast', 1: 'Balanced · medium roast', 2: 'Deep · dark roast' };
    var html = '';
    [0, 1, 2].forEach(function (g) {
      if (!groups[g].length) return;
      html += '<div class="grp"><div class="grplbl">' + lbl[g] + '</div><div class="pchips">' +
        groups[g].sort().map(function (n) {
          var on = state.selectedNotes.indexOf(n) >= 0, dis = !on && state.selectedNotes.length >= 5;
          return '<button type="button" class="imv-pill' + (on ? ' on' : '') + '" data-n="' + escapeHtml(n) + '"' + (dis ? ' disabled' : '') + '>' + cap(n) + '</button>';
        }).join('') + '</div></div>';
    });
    host.innerHTML = html;
    host.querySelectorAll('.imv-pill').forEach(function (b) { b.addEventListener('click', function () { toggleNote(b.dataset.n); }); });
    $('tasteCnt').textContent = state.selectedNotes.length + ' / 5';
    updateTasteBack(map);
  }
  function toggleNote(n) {
    var i = state.selectedNotes.indexOf(n);
    if (i >= 0) state.selectedNotes.splice(i, 1);
    else { if (state.selectedNotes.length >= 5) return; state.selectedNotes.push(n); }
    recomputeFromNotes(); renderPalette();
  }
  function recomputeFromNotes() {
    var sel = state.selectedNotes; if (!sel.length) return;
    var map = availableNotes(), dsum = 0;
    sel.forEach(function (n) { dsum += noteBand(map, n); });
    var idx = clamp(Math.round((dsum / sel.length) / 2 * 6), 0, 6);
    applyPreset(PRESETS[idx]);
    var scores = {}, tot = 0;
    state.order.forEach(function (k) { var s = 0.3; sel.forEach(function (n) { if (map[n] && map[n].origins.indexOf(k) >= 0) s += 1; }); scores[k] = s; tot += s; });
    if (tot > 0) { state.order.forEach(function (k) { state.shares[k] = clamp(scores[k] / tot * 100, 1, 99); }); renorm(); reorder(); renderWeights(true); }
    render();  // the preset glide re-renders anyway; reduced-motion needs this one
  }
  function updateTasteBack(map) {
    var back = $('tasteBack'), sel = state.selectedNotes;
    if (!sel.length) { back.hidden = true; return; }
    map = map || availableNotes();
    var dsum = 0; sel.forEach(function (n) { dsum += noteBand(map, n); });
    var idx = clamp(Math.round((dsum / sel.length) / 2 * 6), 0, 6);
    var m = model(), aIdx = bandIdx[m.band], will = [], faded = [], need = [];
    sel.forEach(function (n) { var b = Math.round(noteBand(map, n)); if (b === aIdx) will.push(n); else if (b < aIdx) faded.push(n); else need.push(n); });
    var msg = 'Roasting to <b>' + PRESETS[idx].name + '</b> (' + m.level + ') and pulling your origins toward these flavours.';
    if (will.length) msg += ' This brings out ' + will.join(', ') + '.';
    if (faded.length) msg += ' ' + cap(faded.join(', ')) + ' sit' + (faded.length > 1 ? '' : 's') + ' in a lighter roast and soften' + (faded.length > 1 ? '' : 's') + ' here.';
    if (need.length) msg += ' ' + cap(need.join(', ')) + ' need' + (need.length > 1 ? '' : 's') + ' a darker roast to emerge.';
    back.hidden = false; back.innerHTML = msg;
  }

  /* ===== THE GLOW: show where Mwamba's hands went ===== */
  function glow(sel) {
    document.querySelectorAll(sel).forEach(function (el) {
      el.classList.remove('imv-glow'); void el.offsetWidth; el.classList.add('imv-glow');
      setTimeout(function () { el.classList.remove('imv-glow'); }, 1200);
    });
  }

  /* ===== MWAMBA ===== */
  var chatLog = [], chatlogEl = $('chatlog'), chatInput = $('chatinput');
  var mwambaLive = false;
  function renderChat() {
    chatlogEl.innerHTML = chatLog.map(function (m) {
      if (m.role === 'applied') return '<div class="imv-msg applied">' + escapeHtml(m.content) + '</div>';
      return '<div class="imv-msg ' + (m.role === 'user' ? 'u' : 'r') + '">' + escapeHtml(m.content) + '</div>';
    }).join('');
    chatlogEl.scrollTop = chatlogEl.scrollHeight;
  }
  function pushMsg(role, content) { chatLog.push({ role: role, content: content }); renderChat(); }
  function showTyping() {
    var t = document.createElement('div'); t.className = 'imv-msg r typing'; t.id = '__typing';
    t.innerHTML = '<span class="d"></span><span class="d"></span><span class="d"></span>';
    chatlogEl.appendChild(t); chatlogEl.scrollTop = chatlogEl.scrollHeight;
  }
  function hideTyping() { var t = document.getElementById('__typing'); if (t) t.remove(); }
  var _typeTimer = null, _typeDone = null;
  function _flushTyping() {
    // a superseded message still delivers its actions/chips before dying
    clearInterval(_typeTimer);
    if (_typeDone) { var d = _typeDone; _typeDone = null; d(); }
  }
  function pushAssistantTyped(text, onDone) {
    chatLog.push({ role: 'assistant', content: text }); renderChat();
    if (reduceMotion) { if (onDone) onDone(); return; }
    var bubs = chatlogEl.querySelectorAll('.imv-msg.r'), el = bubs[bubs.length - 1];
    if (!el) { if (onDone) onDone(); return; }
    el.textContent = ''; var i = 0, step = Math.max(2, Math.ceil(text.length / 90));
    _flushTyping();
    _typeDone = onDone || null;
    _typeTimer = setInterval(function () {
      i += step; el.textContent = text.slice(0, i); chatlogEl.scrollTop = chatlogEl.scrollHeight;
      if (i >= text.length) { clearInterval(_typeTimer); el.textContent = text; if (_typeDone) { var d = _typeDone; _typeDone = null; d(); } }
    }, 18);
  }
  function findPreset(name) { name = String(name || '').toLowerCase().trim(); for (var i = 0; i < PRESETS.length; i++) { if (PRESETS[i].id === name || PRESETS[i].name.toLowerCase() === name) return PRESETS[i]; } return null; }
  function setDropTemp(target) {
    target = clamp(+target, 196, 236); clearPresetActive();
    if (reduceMotion) { state.points[4].temp = target; constrain(4); render(); return; }
    var from = state.points[4].temp, t0 = performance.now(), dur = 440;
    cancelAnimationFrame(anim);
    (function step(now) {
      var r = clamp((now - t0) / dur, 0, 1), e = r < .5 ? 2 * r * r : 1 - Math.pow(-2 * r + 2, 2) / 2;
      state.points[4].temp = from + (target - from) * e; constrain(4); render();
      if (r < 1) anim = requestAnimationFrame(step);
    })(t0);
  }
  function setBlendAction(origins) {
    var valid = origins.filter(function (o) { return o && ORIGINS[o.key]; });
    if (!valid.length) return false;
    state.shares = {}; state.order = [];
    valid.forEach(function (o) { state.shares[o.key] = clamp(+o.pct || 10, 1, 99); state.order.push(o.key); });
    renorm(); reorder(); renderWeights(true); refreshPalette(); return true;
  }
  function applyActions(actions) {
    if (!Array.isArray(actions)) return null;
    var list = actions.slice(0, 3);
    // A preset followed by a curve nudge is one movement, not two: land the
    // preset instantly so the curve animation doesn't fight (and cancel) it.
    var hasCurve = list.some(function (a) { return a && (a.type === 'setCurve' || a.type === 'setRoastLevel'); });
    var done = [], blendChanged = false;
    list.forEach(function (a) {
      if (!a || !a.type) return;
      if (a.type === 'setBlend' && Array.isArray(a.origins)) { if (setBlendAction(a.origins)) { blendChanged = true; done.push('blend'); glow('#panel-origins'); } }
      else if (a.type === 'setPreset' && a.name) {
        var p = findPreset(a.name);
        if (p) {
          if (hasCurve) {
            state.preset = p.id;
            for (var i = 0; i < 4; i++) { state.points[i + 1].t = p.pts[i].t; state.points[i + 1].temp = p.pts[i].temp; }
            render();
          } else applyPreset(p);
          done.push(p.name); glow('#panel-roast');
        }
      }
      else if ((a.type === 'setCurve' || a.type === 'setRoastLevel') && a.dropTemp != null) {
        var lo = Math.max(196, state.points[3].temp + 2);
        var applied = clamp(Math.round(+a.dropTemp), lo, 236);
        setDropTemp(applied); done.push('drop ' + applied + '°C'); glow('#panel-roast');
      }
    });
    if (blendChanged) render();
    return done.length ? ('Mwamba adjusted ' + done.join(' · ')) : null;
  }
  function chatState() {
    var m = model(), P = pctMap();
    return {
      blend: state.order.map(function (k) { return { key: k, name: ORIGINS[k].name, pct: P[k] }; }),
      roast: { level: m.level, dropTemp: Math.round(m.dropTemp), developmentPct: m.fcReached ? Math.round(m.DTR * 100) : 0 },
      notes: m.notes
    };
  }
  /* Not every failure body is JSON (a proxy 502 is plain text): read safely. */
  function safeJson(r) {
    return r.json().catch(function () { return {}; }).then(function (j) { return { ok: r.ok, j: j || {} }; });
  }
  var URL_RE = /^(https?:\/\/)?[\w-]+(\.[\w-]+)+(\/\S*)?$/i;
  var URL_IN_TEXT_RE = /https?:\/\/[^\s<>"']+|(?:www\.)?[\w-]+(?:\.[\w-]+)+(?:\/[^\s<>"']*)?/i;
  function coffeeReference(text) {
    var match = String(text || '').match(URL_IN_TEXT_RE);
    if (!match && URL_RE.test(text)) match = [text];
    if (!match) return null;
    var rawUrl = match[0], url = rawUrl.replace(/[),.;!?]+$/, '');
    var host = url.replace(/^https?:\/\//i, '').split('/')[0];
    var tld = host.split('.').pop();
    if (!/^[a-z]{2,}$/i.test(tld)) return null;
    if (!/^https?:\/\//i.test(url)) url = 'https://' + url;
    var coffee = String(text).replace(rawUrl, ' ').replace(/\s+/g, ' ').trim();
    coffee = coffee.replace(/^(?:coffee\s*:?\s*|it(?:'s| is)\s+|i\s+(?:mean|like|love|want)\s+)/i, '')
      .replace(/^(?:[-—:]\s*)+|(?:\s*[-—:])+$/g, '').trim();
    return { url: url, coffee: coffee, display: String(text).trim() };
  }
  function sendChat(text) {
    text = (text || chatInput.value || '').trim(); if (!text) return;
    chatInput.value = '';
    var reference = coffeeReference(text);
    if (reference) { importLink(reference.url, reference.coffee, reference.display); return; }
    pushMsg('user', text);
    if (!API) { pushAssistantTyped('The roaster steps out while the lab is being connected. Shape the roast by hand — everything here still answers to you.'); return; }
    var history = chatLog.filter(function (m) { return m.role === 'user' || m.role === 'assistant'; }).slice(-12)
      .map(function (m) { return { role: m.role, content: m.content }; });
    showTyping();
    fetch(API + '/chat', { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ state: chatState(), messages: history }) })
      .then(safeJson)
      .then(function (res) {
        hideTyping();
        if (!res.ok || !res.j.reply) throw new Error((res.j && res.j.error) || '');
        pushAssistantTyped(res.j.reply, function () {
          var summary = applyActions(res.j.actions);
          if (summary) pushMsg('applied', summary);
          renderChips(res.j.suggestions);
        });
      })
      .catch(function (e) {
        hideTyping();
        pushAssistantTyped(String(e && e.message || '').trim() || 'I lost that one in the roaster smoke. Ask me again in a moment.');
      });
  }
  function renderImportChoices(list, url) {
    var items = (Array.isArray(list) ? list : []).slice(0, 6);
    if (!items.length) return false;
    var chipHost = $('chatchips');
    chipHost.innerHTML = items.map(function (name) {
      return '<button type="button" class="imv-pill">' + escapeHtml(name) + '</button>';
    }).join('');
    chipHost.querySelectorAll('.imv-pill').forEach(function (button) {
      button.addEventListener('click', function () {
        importLink(url, button.textContent, button.textContent);
      });
    });
    return true;
  }
  function importLink(url, coffee, displayText) {
    url = String(url || '').trim();
    coffee = String(coffee || '').trim();
    if (!url) { toast('Paste the coffee page in your message.'); chatInput.focus(); return; }
    pushMsg('user', displayText || (coffee ? (coffee + ' — ' + url) : url));
    if (!API) { pushAssistantTyped('I can read a coffee from a link once the lab is connected. For now, tell me the cup in your own words.'); return; }
    var reading = coffee ? ('Finding ' + coffee + ' …') : 'Reading the coffee page …';
    pushMsg('applied', reading);
    showTyping();
    fetch(API + '/import', { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url: url, coffee: coffee }) })
      .then(safeJson)
      .then(function (res) {
        hideTyping();
        chatLog = chatLog.filter(function (m) { return m.content !== reading; });
        if (!res.ok || !res.j.reply) throw new Error((res.j && res.j.error) || '');
        if (res.j.coffee && (res.j.coffee.name || res.j.coffee.origin)) {
          pushMsg('applied', 'Read: ' + [res.j.coffee.name, res.j.coffee.roaster, res.j.coffee.origin].filter(Boolean).join(' · '));
        }
        pushAssistantTyped(res.j.reply, function () {
          var hasChoices = renderImportChoices(res.j.choices, url);
          var summary = applyActions(res.j.actions);
          if (summary) pushMsg('applied', summary);
          if (!hasChoices) renderChips(res.j.suggestions);
        });
      })
      .catch(function (e) {
        hideTyping();
        chatLog = chatLog.filter(function (m) { return m.content !== reading; }); renderChat();
        pushAssistantTyped(String(e && e.message || '').trim() || 'I could not read that page. Name the coffee and tell me how it tasted — I can still translate the cup.');
      });
  }
  /* "Bring a coffee you love": the same field reads links, so only the
     hand is guided there — no staged messages. */
  function beginCoffeeShare() {
    chatInput.placeholder = 'Paste the coffee’s link. Add its name if the page lists several.';
    var hint = $('chathint'); if (hint) hint.textContent = 'Mwamba reads the page and finds its closest Imvelo match.';
    chatInput.focus({ preventScroll: true });
    chatInput.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
  }
  var DEFAULT_CHIPS = ['Smooth, never bitter', 'Bright and fruity', 'Dark and strong', 'What would you roast for me?'];
  function renderChips(list) {
    var items = (Array.isArray(list) && list.length ? list : DEFAULT_CHIPS).slice(0, 4);
    var chipHost = $('chatchips');
    chipHost.innerHTML = items.map(function (c) { return '<button type="button" class="imv-pill">' + escapeHtml(c) + '</button>'; }).join('');
    chipHost.querySelectorAll('.imv-pill').forEach(function (b) { b.addEventListener('click', function () { sendChat(b.textContent); }); });
  }
  (function initChat() {
    renderChips(DEFAULT_CHIPS);
    var form = $('chatform');
    if (form) form.addEventListener('submit', function (e) { e.preventDefault(); sendChat(); });
    var linkBtn = $('linkBtn');
    if (linkBtn) linkBtn.addEventListener('click', beginCoffeeShare);
    document.querySelectorAll('[data-way]').forEach(function (b) {
      b.addEventListener('click', function () {
        var way = b.dataset.way, target = way === 'hand' ? $('panel-origins') : $('imv-mwamba');
        if (target) target.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' });
        if (way === 'link') setTimeout(beginCoffeeShare, reduceMotion ? 0 : 420);
        else if (way === 'chat') setTimeout(function () { chatInput.focus({ preventScroll: true }); }, reduceMotion ? 0 : 420);
      });
    });
    pushMsg('assistant', 'I’m Mwamba, Imvelo’s master roaster in Lusaka. Tell me the cup you want, or paste a link to a coffee you love, and I’ll set the roast with you.');
    var stateEl = $('chatstate');
    if (!API) { if (stateEl) stateEl.textContent = 'Away'; return; }
    fetch(API + '/health').then(function (r) { return r.json(); }).then(function (j) {
      mwambaLive = !!(j && j.ok && j.configured);
      if (stateEl) { stateEl.textContent = mwambaLive ? 'At the drum' : 'Away'; stateEl.classList.toggle('on', mwambaLive); }
    }).catch(function () { if (stateEl) stateEl.textContent = 'Away'; });
  })();

  /* ===== NAMING ===== */
  var NAME_PHRASES = ['Reading your curve…', 'Tasting the cup in our heads…', 'Listening to the beans…', 'Naming your roast…'];
  function fallbackCopy(m) {
    var place = ORIGINS[state.order[0]].name;
    var names = ['Highland Ember', 'Kivu Dusk', 'Red Soil Morning', 'Acacia Light', 'Rift Valley Gold', 'Lusaka Sundown'];
    return { name: names[Math.floor(m.R * 5.99)],
      description: 'A ' + m.level.toLowerCase() + ' roast leading with ' + m.notes.slice(0, 2).join(' and ') +
        ', carried on a ' + (m.f.body > 6 ? 'full, syrupy' : 'clean, supple') + ' body. Rooted in ' + place +
        ' and roasted by hand in Lusaka.' };
  }
  $('aibtn').addEventListener('click', function () {
    if (!state.order.length) { toast('Add at least one origin first.'); return; }
    var m = model(), btn = $('aibtn'); btn.disabled = true;
    var t0 = performance.now(), minDur = 1800 + Math.random() * 900, ph = 0;
    var setPhase = function () { btn.textContent = NAME_PHRASES[ph % NAME_PHRASES.length]; ph++; };
    setPhase(); var phaseTimer = setInterval(setPhase, 750);
    var Pa = pctMap();
    var finish = function (name, description) {
      var wait = Math.max(0, minDur - (performance.now() - t0));
      setTimeout(function () {
        clearInterval(phaseTimer);
        state.name = name; state.description = description || '';
        var Pc = pctMap(); state.descSig = state.order.map(function (k) { return k + ':' + Pc[k]; }).join('|') + '|' + m.level;
        $('roastName').value = name; syncTitle();
        $('namedesc').textContent = state.description; $('namedesc').hidden = !state.description;
        glow('#panel-cup');
        btn.disabled = false; btn.textContent = 'Let Mwamba name it';
        persistSoon();
      }, wait);
    };
    var local = function () { var fb = fallbackCopy(m); finish(fb.name, fb.description); };
    if (!API) { local(); return; }
    fetch(API + '/compose', { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ blend: state.order.map(function (k) { return { key: k, pct: Pa[k] }; }),
        level: m.level, notes: m.notes }) })
      .then(function (r) { return r.json(); })
      .then(function (j) { if (j && j.name) finish(j.name, j.description); else local(); })
      .catch(local);
  });

  /* ===== SAVE · SHARE · EXPORTS ===== */
  var STORE_KEY = 'imvelo_roast_v3';
  var _persistT = null;
  function persistSoon() { clearTimeout(_persistT); _persistT = setTimeout(persistNow, 500); }
  function persistNow() { try { if (state.order.length) localStorage.setItem(STORE_KEY, encodeRoast()); } catch (e) {} }
  window.addEventListener('pagehide', persistNow);
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') persistNow(); });
  function b64urlEncode(str) { return btoa(unescape(encodeURIComponent(str))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''); }
  function b64urlDecode(str) { str = str.replace(/-/g, '+').replace(/_/g, '/'); return decodeURIComponent(escape(atob(str))); }
  function encodeRoast() { return b64urlEncode(JSON.stringify({ w: state.shares, o: state.order, p: state.points, n: state.name || '', d: state.description || '' })); }
  function applyRoastToken(tok) {
    try {
      var o = JSON.parse(b64urlDecode(tok));
      if (o.w && Array.isArray(o.o)) {
        var w = {}, ord = [];
        o.o.forEach(function (k) { if (ORIGINS[k] && o.w[k] != null) { w[k] = +o.w[k]; ord.push(k); } });
        if (ord.length) { state.shares = w; state.order = ord; }
      }
      if (Array.isArray(o.p) && o.p.length === 5) { state.points = o.p.map(function (pt) { return { t: +pt.t, temp: +pt.temp }; }); state.preset = null; }
      state.name = o.n || ''; state.description = o.d || '';
      return true;
    } catch (e) { return false; }
  }
  function shareUrl() { return location.origin + location.pathname + '#r=' + encodeRoast(); }
  var shareToggle = $('shareToggle'), shareSheet = $('shareSheet'), sheetReturn = null;
  function openSheet() {
    if (!shareSheet) return;
    if (!state.order.length) { toast('Add an origin to your blend first.'); return; }
    sheetReturn = document.activeElement; shareSheet.hidden = false; document.body.style.overflow = 'hidden';
    var first = shareSheet.querySelector('.imv-sheet__list button'); if (first) first.focus({ preventScroll: true });
  }
  function closeSheet() {
    if (!shareSheet || shareSheet.hidden) return;
    shareSheet.hidden = true; document.body.style.overflow = '';
    if (sheetReturn && sheetReturn.focus) sheetReturn.focus({ preventScroll: true });
  }
  if (shareToggle) shareToggle.addEventListener('click', openSheet);
  if (shareSheet) {
    shareSheet.querySelectorAll('[data-sheet-close]').forEach(function (el) { el.addEventListener('click', closeSheet); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeSheet(); });
  }
  /* the share rows carry a title and a caption: only the title changes while busy */
  function busy(btn, on, label) {
    if (!btn) return;
    var t = btn.querySelector('b') || btn;
    if (on) { btn.disabled = true; btn.dataset.old = t.textContent; t.textContent = label; }
    else { btn.disabled = false; if (btn.dataset.old) t.textContent = btn.dataset.old; }
  }
  $('shareBtn').addEventListener('click', function () {
    if (!state.order.length) { toast('Add an origin to your blend first.'); return; }
    var url = shareUrl();
    closeSheet();
    if (navigator.share) navigator.share({ title: state.name || 'My Imvelo roast', text: 'I roasted this with Imvelo', url: url }).catch(function () {});
    else (navigator.clipboard ? navigator.clipboard.writeText(url) : Promise.reject())
      .then(function () { toast('Share link copied.'); })
      .catch(function () { window.prompt('Copy your share link', url); });
  });

  /* --- canvas exports: the roast sheet, Edition 02 on navy --- */
  function ensureFonts() {
    var loads = [
      document.fonts.load('500 72px "Bodoni Moda"'),
      document.fonts.load('italic 500 34px "Bodoni Moda"'),
      document.fonts.load('300 30px "Hanken Grotesk"'),
      document.fonts.load('400 22px "Space Mono"')
    ];
    return Promise.all(loads).then(function () { return document.fonts.ready; }).catch(function () {});
  }
  var _qrLib = null;
  function loadQRLib() {
    if (window.QRCode) return Promise.resolve(true);
    if (_qrLib) return _qrLib;
    _qrLib = new Promise(function (res) {
      var sc = document.createElement('script');
      sc.src = 'https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js';
      sc.onload = function () { res(true); }; sc.onerror = function () { res(false); };
      document.head.appendChild(sc);
    });
    return _qrLib;
  }
  function makeQR(url, size) {
    return loadQRLib().then(function (ok) {
      if (!ok || !window.QRCode) return null;
      var holder = document.createElement('div');
      holder.style.cssText = 'position:absolute;left:-9999px;top:-9999px';
      document.body.appendChild(holder);
      return new Promise(function (res) {
        try {
          new window.QRCode(holder, { text: url, width: size, height: size, colorDark: '#141F38', colorLight: '#ffffff', correctLevel: window.QRCode.CorrectLevel.M });
          setTimeout(function () {
            var cv = holder.querySelector('canvas');
            if (cv) { res(cv); }
            else {
              var img = holder.querySelector('img');
              if (img && img.src) { var im = new Image(); im.onload = function () { res(im); }; im.onerror = function () { res(null); }; im.src = img.src; }
              else res(null);
            }
            setTimeout(function () { holder.remove(); }, 2000);
          }, 90);
        } catch (e) { holder.remove(); res(null); }
      });
    });
  }
  function _track(ctx, text, x, y, sp) { var cx = x; String(text).split('').forEach(function (ch) { ctx.fillText(ch, cx, y); cx += ctx.measureText(ch).width + sp; }); }
  function _wrap(ctx, text, x, y, maxW, lh) {
    var words = String(text).split(/\s+/), line = '', cy = y; ctx.textAlign = 'left';
    for (var i = 0; i < words.length; i++) {
      var t = line ? line + ' ' + words[i] : words[i];
      if (ctx.measureText(t).width > maxW && line) { ctx.fillText(line, x, cy); line = words[i]; cy += lh; } else line = t;
    }
    if (line) ctx.fillText(line, x, cy);
    return cy;
  }
  function _sheetRadar(ctx, cx, cy, R, m) {
    var n = AXES.length, g, i, a, rr;
    ctx.lineWidth = 1;
    for (g = 1; g <= 4; g++) {
      rr = R * g / 4; ctx.beginPath();
      for (i = 0; i < n; i++) { a = -Math.PI / 2 + i * 2 * Math.PI / n; var px = cx + rr * Math.cos(a), py = cy + rr * Math.sin(a); i ? ctx.lineTo(px, py) : ctx.moveTo(px, py); }
      ctx.closePath(); ctx.strokeStyle = 'rgba(239,232,214,0.16)'; ctx.stroke();
    }
    for (i = 0; i < n; i++) { a = -Math.PI / 2 + i * 2 * Math.PI / n; ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(cx + R * Math.cos(a), cy + R * Math.sin(a)); ctx.strokeStyle = 'rgba(239,232,214,0.16)'; ctx.stroke(); }
    ctx.beginPath();
    AXES.forEach(function (ax, j) { a = -Math.PI / 2 + j * 2 * Math.PI / n; rr = R * (m.f[ax[0]] / 10); var px = cx + rr * Math.cos(a), py = cy + rr * Math.sin(a); j ? ctx.lineTo(px, py) : ctx.moveTo(px, py); });
    ctx.closePath();
    ctx.fillStyle = 'rgba(224,164,59,0.16)'; ctx.fill();
    ctx.strokeStyle = '#E0A43B'; ctx.lineWidth = 2; ctx.stroke();
    ctx.font = '400 19px "Hanken Grotesk", sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    AXES.forEach(function (ax, j) {
      a = -Math.PI / 2 + j * 2 * Math.PI / n; rr = R * (m.f[ax[0]] / 10);
      var px = cx + rr * Math.cos(a), py = cy + rr * Math.sin(a);
      ctx.beginPath(); ctx.arc(px, py, 4, 0, 7); ctx.fillStyle = '#EFE8D6'; ctx.fill();
      ctx.fillStyle = 'rgba(239,232,214,0.6)';
      ctx.fillText(ax[1], cx + (R + 28) * Math.cos(a), cy + (R + 28) * Math.sin(a));
    });
    ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  }
  function _dl(blob, fname) {
    var a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = fname;
    document.body.appendChild(a); a.click();
    setTimeout(function () { URL.revokeObjectURL(a.href); a.remove(); }, 1500);
  }
  function buildCardCanvas() {
    var W = 1080, M = 96, m = model(), P = pctMap();
    var name = (state.name || roastTitle() || 'My Imvelo roast').trim();
    var desc = (state.description || '').trim();
    var url = shareUrl();
    return ensureFonts().then(function () { return makeQR(url, 210); }).then(function (qr) {
      var SCRATCH = 3200;
      var c = document.createElement('canvas'); c.width = W; c.height = SCRATCH;
      var x = c.getContext('2d');
      x.fillStyle = '#141F38'; x.fillRect(0, 0, W, SCRATCH);
      x.textAlign = 'left'; x.textBaseline = 'alphabetic';
      var y = 124;
      x.fillStyle = '#E0A43B'; x.font = '400 23px "Space Mono", monospace';
      _track(x, 'IMVELO · THE ROAST LAB · LUSAKA', M, y, 5); y += 40;
      x.strokeStyle = 'rgba(239,232,214,0.30)'; x.lineWidth = 1;
      x.beginPath(); x.moveTo(M, y); x.lineTo(W - M, y); x.stroke(); y += 78;
      x.fillStyle = '#EFE8D6'; x.font = '500 82px "Bodoni Moda", serif';
      y = _wrap(x, name, M, y, W - 2 * M, 92) + 52;
      if (desc) { x.fillStyle = 'rgba(239,232,214,0.62)'; x.font = 'italic 500 31px "Bodoni Moda", serif'; y = _wrap(x, desc, M, y, W - 2 * M, 46) + 6; }
      y += 40;
      var R = 168, ccx = W / 2, ccy = y + R; _sheetRadar(x, ccx, ccy, R, m); y = ccy + R + 72;
      x.fillStyle = '#E0A43B'; x.font = '400 19px "Space Mono", monospace'; _track(x, 'BLEND', M, y, 4); y += 44;
      state.order.forEach(function (k) {
        x.fillStyle = '#EFE8D6'; x.font = '400 33px "Hanken Grotesk", sans-serif'; x.fillText(ORIGINS[k].name, M, y);
        x.fillStyle = '#E0A43B'; x.font = '400 28px "Space Mono", monospace'; x.textAlign = 'right'; x.fillText(P[k] + '%', W - M, y); x.textAlign = 'left';
        y += 46;
      });
      y += 26;
      x.fillStyle = '#E0A43B'; x.font = '400 19px "Space Mono", monospace'; _track(x, 'ROAST', M, y, 4); y += 48;
      x.fillStyle = '#EFE8D6'; x.font = '500 42px "Bodoni Moda", serif'; x.fillText(m.level, M, y); y += 44;
      x.fillStyle = 'rgba(239,232,214,0.62)'; x.font = '300 27px "Hanken Grotesk", sans-serif';
      x.fillText('Drop ' + showTemp(m.dropTemp) + '   ·   development ' + (m.fcReached ? Math.round(m.DTR * 100) + '%' : '—'), M, y); y += 66;
      x.fillStyle = '#E0A43B'; x.font = '400 19px "Space Mono", monospace'; _track(x, 'TASTING NOTES', M, y, 4); y += 48;
      x.fillStyle = '#EFE8D6'; x.font = 'italic 500 38px "Bodoni Moda", serif';
      y = _wrap(x, m.notes.map(cap).join(', '), M, y, W - 2 * M, 50) + 62;
      var qrSize = 210, qrX = M, qrY = y;
      if (qr) { x.fillStyle = '#ffffff'; x.fillRect(qrX - 14, qrY - 14, qrSize + 28, qrSize + 28); x.drawImage(qr, qrX, qrY, qrSize, qrSize); }
      var tx = qr ? qrX + qrSize + 48 : M;
      x.fillStyle = '#EFE8D6'; x.font = '500 38px "Bodoni Moda", serif'; x.fillText('Roast your own', tx, qrY + 58);
      x.fillStyle = 'rgba(239,232,214,0.62)'; x.font = '300 26px "Hanken Grotesk", sans-serif'; x.fillText('Scan to open and make it your own', tx, qrY + 102);
      x.fillStyle = '#E0A43B'; x.font = '400 24px "Space Mono", monospace'; x.fillText('imvelobrands.com', tx, qrY + 150);
      var H = Math.ceil(qrY + qrSize + 96);
      var out = document.createElement('canvas'); out.width = W; out.height = H;
      out.getContext('2d').drawImage(c, 0, 0, W, H, 0, 0, W, H);
      return { canvas: out, name: name };
    });
  }
  function slugify(s) { return String(s).replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase(); }
  function exportSheet(format) {
    if (!state.order.length) { toast('Add an origin to your blend first.'); return; }
    var id = format === 'story' ? 'storyBtn' : 'sheetBtn', btn = $(id);
    busy(btn, true, 'Creating…');
    buildCardCanvas().then(function (out) {
      var W = 1080, minH = format === 'story' ? 1920 : 1350, H = Math.max(minH, out.canvas.height);
      var fc = document.createElement('canvas'); fc.width = W; fc.height = H;
      var fx = fc.getContext('2d');
      fx.fillStyle = '#141F38'; fx.fillRect(0, 0, W, H);
      fx.drawImage(out.canvas, 0, Math.max(0, Math.floor((H - out.canvas.height) / 2)));
      var slug = (slugify(out.name) || 'imvelo') + '-roast' + (format === 'story' ? '-story' : '') + '.png';
      fc.toBlob(function (b) {
        if (!b) { toast('Could not create the image.'); return; }
        var file = new File([b], slug, { type: 'image/png' });
        var url = shareUrl();
        var shareData = {
          files: [file],
          title: out.name,
          text: 'Open this Imvelo roast and make it your own',
          url: url
        };
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          navigator.share(shareData).catch(function (e) {
            if (e && e.name === 'AbortError') return;
            _dl(b, slug);
            if (format === 'story') toast('Story saved. Use Share editable link for a clickable link.');
          });
        } else {
          _dl(b, slug);
          if (format === 'story') toast('Story saved. Use Share editable link for a clickable link.');
        }
      }, 'image/png');
    }).catch(function () { toast('Could not create the image.'); })
      .then(function () { busy(btn, false); });
  }
  var _pdfLib = null;
  function loadPDFLib() {
    if (window.jspdf && window.jspdf.jsPDF) return Promise.resolve(true);
    if (_pdfLib) return _pdfLib;
    _pdfLib = new Promise(function (res) {
      var sc = document.createElement('script');
      sc.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      sc.onload = function () { res(true); }; sc.onerror = function () { res(false); };
      document.head.appendChild(sc);
    });
    return _pdfLib;
  }
  function exportPDF() {
    if (!state.order.length) { toast('Add an origin to your blend first.'); return; }
    var btn = $('pdfBtn');
    busy(btn, true, 'Creating…');
    buildCardCanvas().then(function (out) {
      return loadPDFLib().then(function (ok) {
        if (!ok || !window.jspdf || !window.jspdf.jsPDF) { toast('Could not load the PDF tool.'); return; }
        var data = out.canvas.toDataURL('image/png');
        var pdf = new window.jspdf.jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
        var pw = pdf.internal.pageSize.getWidth(), ph = pdf.internal.pageSize.getHeight();
        pdf.setFillColor(20, 31, 56); pdf.rect(0, 0, pw, ph, 'F');
        var margin = 22, ar = out.canvas.width / out.canvas.height;
        var h = ph - 2 * margin, w = h * ar;
        if (w > pw - 2 * margin) { w = pw - 2 * margin; h = w / ar; }
        pdf.addImage(data, 'PNG', (pw - w) / 2, (ph - h) / 2, w, h);
        pdf.save((slugify(out.name) || 'imvelo') + '-roast.pdf');
      });
    }).catch(function () { toast('Could not create the PDF.'); })
      .then(function () { busy(btn, false); });
  }
  $('sheetBtn').addEventListener('click', function () { exportSheet('feed'); });
  $('storyBtn').addEventListener('click', function () { exportSheet('story'); });
  $('pdfBtn').addEventListener('click', exportPDF);

  /* ===== INIT ===== */
  (function restoreRoast() {
    var mtok = location.search.match(/[?&]r=([^&]+)/) || location.hash.match(/[#&]r=([^&]+)/);
    if (mtok) {
      var tok = decodeURIComponent(mtok[1]);
      if (applyRoastToken(tok)) {
        try { localStorage.setItem(STORE_KEY, tok); } catch (e) {}
        setTimeout(function () { toast('Opened a shared roast.'); }, 350);
      }
    } else {
      try {
        var sv = localStorage.getItem(STORE_KEY) || localStorage.getItem('imvelo_roast_v2');
        if (sv) applyRoastToken(sv);
      } catch (e) {}
    }
  })();
  renderWeights(true); render();
  if (state.mode === 'taste') tasteBox.open = true;
  var dockAdd = $('dockAdd'); if (dockAdd) dockAdd.addEventListener('click', function () { $('addcart').click(); });
  /* the roast module is sticky; if it is still taller than the window (a long name, a small screen), it sticks by
     its foot instead of its head, so the price and the button never leave the screen */
  (function keepModuleInView() {
    var side = document.querySelector('.imv-side'); if (!side) return;
    var fit = function () {
      if (getComputedStyle(side).position !== 'sticky') { side.style.top = ''; return; }
      var head = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--imvelo-header-h')) || 72;
      var top = head + 16, room = window.innerHeight - top - 16, h = side.offsetHeight;
      side.style.top = h > room ? (window.innerHeight - h - 16) + 'px' : '';
    };
    fit(); window.addEventListener('resize', fit);
    if (window.ResizeObserver) new ResizeObserver(fit).observe(side);
  })();
  var cupTitle = $('cupTitle'); if (cupTitle) cupTitle.addEventListener('click', function (e) { e.preventDefault(); $('panel-name').scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' }); setTimeout(function () { $('roastName').focus({ preventScroll: true }); }, reduceMotion ? 0 : 450); });
  var dockSum = $('dockSum'); if (dockSum) dockSum.addEventListener('click', function () { $('panel-cup').scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' }); });
  (function wireName() {
    var ni = $('roastName');
    ni.value = state.name || '';
    ni.addEventListener('input', function () { state.name = ni.value; syncTitle(); persistSoon(); });
    if (state.description) { $('namedesc').textContent = state.description; $('namedesc').hidden = false; }
  })();
})();
